import { ButtonGroup, Form, FormGroup } from "react-bootstrap";
import TooltipTrigger from "../../tooltip-trigger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InvitationRecipient } from "./create-invitation-modal-view";
import UserUtils from "../../../utils/user";
import { useIntl } from "react-intl";
import { OrganizationGroupInvitationLabels } from "../../../localization/organization-group-invitation";
import { UseFormRegister } from "react-hook-form";
import { useId } from "react";
export interface RecipientListItemProps {
  item: InvitationRecipient;
  showEdit: boolean;
  onShowEdit: (f: boolean) => void;
  disableEditButtons: boolean;
  onDismissItem: () => void;
  resolveEditFormValidity: (f: string) => boolean | undefined;
  formValues: any;
  register: UseFormRegister<InvitationRecipient>;
  errors: any;
  formIsValid: boolean;
  onApplyItem: () => void;
  isDuplicate: (s: string) => boolean;
  editTip: string;
  dismissTip: string;
}
export default function RecipientListItem(props: RecipientListItemProps) {
  const {
    item,
    showEdit,
    onShowEdit,
    disableEditButtons,
    onDismissItem,
    resolveEditFormValidity,
    formValues,
    register,
    errors,
    formIsValid,
    onApplyItem,
    isDuplicate,
    editTip,
    dismissTip,
  } = props;
  const intl = useIntl();
  const idPrefix = useId();
  return (
    <li className={"mb-2 d-flex align-items-center item"}>
      {!showEdit && (
        <>
          <span>
            {item.recipientName} ({item.email})
          </span>
          <ButtonGroup size={"sm"} className={"ml-auto"}>
            <TooltipTrigger
              tip={editTip}
              tipKey={"editRecipientsTip"}
              placement={"auto"}
            >
              <button
                data-test-edit-button
                type={"button"}
                disabled={disableEditButtons}
                className={"btn btn-theme-to-primary"}
                onClick={() => {
                  onShowEdit(true);
                }}
              >
                <FontAwesomeIcon icon={"pen"} fixedWidth={true} />
              </button>
            </TooltipTrigger>
            <TooltipTrigger
              tip={dismissTip}
              tipKey={"editRecipientsTip"}
              placement={"auto"}
            >
              <button
                data-test-dismiss-button
                type={"button"}
                disabled={disableEditButtons}
                className="btn btn-theme-to-danger"
                onClick={() => {
                  onDismissItem();
                  onShowEdit(false);
                }}
              >
                <FontAwesomeIcon
                  icon={"trash-alt"}
                  fixedWidth={true}
                ></FontAwesomeIcon>
              </button>
            </TooltipTrigger>
          </ButtonGroup>
        </>
      )}
      {showEdit && (
        <>
          <fieldset className={"input-group mb-4 mt-2"}>
            <FormGroup
              data-test-recipient-name
              controlId={idPrefix + "recipientName"}
              className={"input-group-input-wrapper mb-0"}
            >
              <Form.Control
                defaultValue={item.recipientName}
                {...{
                  className:
                    (resolveEditFormValidity("recipientName") === true
                      ? "is-valid"
                      : resolveEditFormValidity("recipientName") === false
                      ? "is-invalid"
                      : "") +
                    ((formValues ? !!formValues.recipientName : false)
                      ? " has-value"
                      : "") +
                    " " +
                    "form-control",
                }}
                {...register("recipientName", {
                  required: true,
                })}
              />
              <Form.Label className="placeholder-label">
                {intl.formatMessage(
                  OrganizationGroupInvitationLabels.recipientName
                )}
              </Form.Label>
              {errors &&
                errors.recipientName &&
                errors.recipientName.type &&
                errors.recipientName.type === "required" && (
                  <Form.Control.Feedback type="invalid">
                    <FontAwesomeIcon
                      icon={"exclamation-circle"}
                      className={"icon mr-1"}
                    />
                    {intl.formatMessage(
                      {
                        defaultMessage: "{recipientName} is required.",
                        description:
                          "Field validation error. 'recipientName' = Field label for the name of the recipient (OrganizationGroupInvitation.recipientName)",
                      },
                      {
                        recipientName: intl.formatMessage(
                          OrganizationGroupInvitationLabels.recipientName
                        ),
                      }
                    )}
                  </Form.Control.Feedback>
                )}
            </FormGroup>
            <FormGroup
              data-test-email
              controlId={idPrefix + "email"}
              className={"input-group-input-wrapper mb-0"}
            >
              <Form.Control
                defaultValue={item.email}
                {...{
                  className:
                    (resolveEditFormValidity("email") === true
                      ? "is-valid"
                      : resolveEditFormValidity("email") === false
                      ? "is-invalid"
                      : "") +
                    ((formValues ? !!formValues.email : false)
                      ? " has-value"
                      : "") +
                    " " +
                    "form-control",
                }}
                {...register("email", {
                  required: true,
                  validate: (value: string) => {
                    return !isDuplicate(value);
                  },
                  pattern: {
                    value: UserUtils.EMAIL_VALIDATION_PATTERN,
                    message: intl.formatMessage(
                      {
                        defaultMessage: "{email} is not valid.",
                        description:
                          "Field validation error. 'email' = Field label for invitation recipient email (OrganizationGroupInvitation.email)",
                      },
                      {
                        email: intl.formatMessage(
                          OrganizationGroupInvitationLabels.email
                        ),
                      }
                    ),
                  },
                })}
              />
              <Form.Label className="placeholder-label">
                {intl.formatMessage(OrganizationGroupInvitationLabels.email)}
              </Form.Label>
              {errors &&
                errors.email &&
                errors.email.type &&
                errors.email.type === "required" && (
                  <Form.Control.Feedback type="invalid">
                    <FontAwesomeIcon
                      icon={"exclamation-circle"}
                      className={"icon mr-1"}
                    />
                    {intl.formatMessage(
                      {
                        defaultMessage: "{email} is required.",
                        description:
                          "Field validation error. 'email' = Field label for invitation recipient email (OrganizationGroupInvitation.email)",
                      },
                      {
                        email: intl.formatMessage(
                          OrganizationGroupInvitationLabels.email
                        ),
                      }
                    )}
                  </Form.Control.Feedback>
                )}
              {errors &&
                errors.email &&
                errors.email.type &&
                errors.email.type === "pattern" && (
                  <Form.Control.Feedback type="invalid">
                    <FontAwesomeIcon
                      icon={"exclamation-circle"}
                      className={"icon mr-1"}
                    />
                    {errors.email.message}
                  </Form.Control.Feedback>
                )}
              {errors &&
                errors.email &&
                errors.email.type &&
                errors.email.type === "validate" && (
                  <Form.Control.Feedback type="invalid">
                    <FontAwesomeIcon
                      icon={"exclamation-circle"}
                      className={"icon mr-1"}
                    />
                    {intl.formatMessage(
                      {
                        defaultMessage: "{email} must be unique.",
                        description:
                          "Field validation error. 'email' = Field label for invitation recipient email (OrganizationGroupInvitation.email)",
                      },
                      {
                        email: intl.formatMessage(
                          OrganizationGroupInvitationLabels.email
                        ),
                      }
                    )}
                  </Form.Control.Feedback>
                )}
            </FormGroup>
            <TooltipTrigger
              className={"input-group-append"}
              tip={
                formIsValid
                  ? intl.formatMessage({
                      defaultMessage: "Apply",
                      description:
                        "tooltip for recipient list item apply button",
                    })
                  : intl.formatMessage({
                      defaultMessage: "Fix errors to apply",
                      description:
                        "tooltip for disabled recipient list item apply button",
                    })
              }
              tipKey={"editRecipientsTip"}
              placement={"auto"}
              wrapDisabled={true}
            >
              <button
                data-test-resolve-button
                type={"button"}
                className="btn btn-theme-to-primary"
                disabled={!formIsValid}
                onClick={() => {
                  onApplyItem();
                }}
              >
                <span className={"fa-layers fa-fw"}>
                  <FontAwesomeIcon
                    icon="check"
                    mask={"pen"}
                    className={"opacity-50"}
                    transform="grow-4 right-8 down-5"
                  />
                  <FontAwesomeIcon
                    icon="check"
                    transform="shrink-4 right-5 down-5"
                  />
                </span>
              </button>
            </TooltipTrigger>
            <TooltipTrigger
              className="input-group-append"
              tip={intl.formatMessage({
                defaultMessage: "Cancel",
                description: "tooltip for recipient list item cancel button",
              })}
              tipKey={"editRecipientsTip"}
              placement={"auto"}
              wrapDisabled={true}
            >
              <button
                data-test-cancel-button
                type={"button"}
                className="btn btn-theme-to-danger"
                onClick={() => {
                  onShowEdit(false);
                }}
              >
                <span className={"fa-layers fa-fw"}>
                  <FontAwesomeIcon
                    icon="times"
                    mask={"pen"}
                    className={"opacity-50"}
                    transform="grow-4 right-7 down-7"
                  />
                  <FontAwesomeIcon
                    icon="times"
                    transform="shrink-4 right-5 down-5"
                  />
                </span>
              </button>
            </TooltipTrigger>
          </fieldset>
        </>
      )}
    </li>
  );
}
